import goodsSupplierConnectionsFormatted, { TRANSPORT_FIELDS } from './goodsSupplierConnectionsFormatted';

import type { IGoodsSupplierConnection } from '../store/slices/valueChainMap/interfaces/IGoodsSupplierConnection';
import type { IManufactureConnection } from '../store/slices/valueChainMap/interfaces/IManufactureConnection';
import type IUnitRef from './interfaces/IUnitRef';

// A product's sources are shown as one list but stored in two tables, so every row carries the
// kind that decides which array it is sent in and which fields its card renders.
export const SOURCING_KINDS = {
  GOODS_SUPPLIER: 'goodsSupplier',
  MANUFACTURE: 'manufacture',
} as const;

export type TSourcingKind = typeof SOURCING_KINDS[keyof typeof SOURCING_KINDS];

// The routing rule is the backend's: it derives `subtype` from the asset categories and seeds
// imports through the same predicate, so reading the flag here keeps the picker and the importer
// in agreement. Anything that is not explicitly a factory is a goods supplier.
export const getSourcingKind = (ref: IUnitRef): TSourcingKind => (
  ref.subtype === 'factory' ? SOURCING_KINDS.MANUFACTURE : SOURCING_KINDS.GOODS_SUPPLIER
);

type TOption = { value?: string, label?: string };

// One row of the joined sourcing list. Only the fields belonging to the row's `kind` are filled
// in; the rest stay untouched and are never read. `fromName` and `fromType` are display-only —
// they render the chip and are dropped on the way out, since the DTO whitelists its properties.
export type TSourcingRow = {
  // Widened to `string` because this is what the form actually holds: the schema types it as a
  // plain required string, and narrowing here would only force a cast at every boundary. The
  // narrow union survives where it is genuinely known — `getSourcingKind`'s return type.
  kind: string,
  fromUnitId: number,
  fromName?: string,
  fromType?: string,
  id?: number,
  distance?: string,
  monetaryVolumes?: string,
  // Unset is an empty object, never null — that is what the enum selector renders as "nothing
  // chosen", and what the schema infers for a conditionally required select.
  unitMonetaryVolumes?: TOption,
  quantity?: string,
  unitCurrency?: TOption,
  manufactureConnectionType?: TOption,
  commentManufacture?: string,
} & Partial<Record<typeof TRANSPORT_FIELDS[number], boolean>>;

// The enum selector builds every option as `{ label: value, value }`, so a stored value needs no
// lookup to become the option a Select renders. An empty object rather than `undefined` for the
// unset case, matching what the schema infers for a conditionally required select.
const toOption = (value: string | null) => (value ? { value, label: value } : {});

const toManufactureRow = (connection: IManufactureConnection): TSourcingRow => ({
  kind: SOURCING_KINDS.MANUFACTURE,
  id: connection.id,
  fromUnitId: connection.fromUnitId,
  fromName: connection.from?.name || '',
  fromType: connection.from?.type || '',
  manufactureConnectionType: toOption(connection.connectionType),
  commentManufacture: connection.comment || '',
});

// Hydrates a record's two connection arrays into the single list the sourcing panel edits.
//
// Returns `undefined` when the record reported neither array, which is what lets `split` omit
// both keys rather than send `[]` and delete. Both arrays present but empty is different
// information — a loaded record with no sources — and hydrates to an empty list.
export const sourcingConnectionsFormatted = (
  goodsSupplier: IGoodsSupplierConnection[] | undefined,
  manufacture: IManufactureConnection[] | undefined,
): TSourcingRow[] | undefined => {
  if (!goodsSupplier && !manufacture) {
    return undefined;
  }

  return [
    ...goodsSupplierConnectionsFormatted(goodsSupplier || [])
      .map((row) => ({ ...row, kind: SOURCING_KINDS.GOODS_SUPPLIER })),
    ...(manufacture || []).map(toManufactureRow),
  ];
};

const isManufacture = (row: TSourcingRow) => row.kind === SOURCING_KINDS.MANUFACTURE;

const toNumber = (value?: string) => (value ? Number(value.replace(/[,%]/g, '')) : null);

// No id means "create this edge". Spreading an absent one rather than writing `id: undefined`
// keeps the key off the payload entirely, which is what the DTO expects.
const withId = ({ id }: TSourcingRow) => (id ? { id } : {});

const toGoodsSupplierConnection = (row: TSourcingRow) => ({
  ...withId(row),
  fromUnitId: row.fromUnitId,
  transportMethods: TRANSPORT_FIELDS.filter((field) => row[field]),
  distance: row.distance || null,
  monetaryValue: toNumber(row.monetaryVolumes),
  monetaryCurrency: row.unitMonetaryVolumes?.value || null,
  quantity: toNumber(row.quantity),
  quantityUnit: row.unitCurrency?.value || null,
});

const toManufactureConnection = (row: TSourcingRow) => ({
  ...withId(row),
  fromUnitId: row.fromUnitId,
  connectionType: row.manufactureConnectionType?.value || null,
  comment: row.commentManufacture || null,
});

// Splits the joined list back into the two arrays the API stores separately.
//
// `undefined` in means both keys are omitted, which the backend reads as "leave those edges
// alone". An empty array in means both are sent as `[]`, which deletes. Collapsing those two
// cases would let a form that never loaded delete every source on an unrelated save — so the
// distinction is the point of this function, not an edge case in it.
//
// Both arrays are always sent together once the list has hydrated, including the empty side:
// they come from one list, so one having rows is proof the other's emptiness is real.
export const splitSourcingConnections = (rows: TSourcingRow[] | undefined) => {
  if (!rows) {
    return {};
  }

  return {
    // Manufacture is the exception and goods supplier the default, mirroring the routing rule —
    // so the split is total. A row whose kind went missing lands in the goods-supplier array,
    // which is the legacy shape, rather than being silently dropped from both.
    goodsSupplierConnections: rows
      .filter((row) => !isManufacture(row))
      .map(toGoodsSupplierConnection),
    manufactureConnections: rows
      .filter(isManufacture)
      .map(toManufactureConnection),
  };
};
